/**
 * Parallel-compute facade over `@nodets/core` (+ its SolidJS adapter).
 *
 * The whole app imports pool primitives from HERE (`@utils/parallel/poolManager`) rather than from
 * `@nodets/*` directly, so we have one place to set defaults, instrument, or swap the backend.
 *
 * Why this exists / what it standardizes:
 *  - **Inline fallback everywhere.** `@nodets` already defaults `fallback:'inline'` (runs the task on
 *    the main thread, yielding between items, when `Worker` is unavailable). We keep that default and
 *    never override it to `'throw'` in product code — it is what keeps Vitest/jsdom (no `Worker`) and
 *    any worker-hostile browser safe. `createPool()` re-asserts it explicitly for readability.
 *  - **Self-contained tasks only.** `@nodets` ships a task to the worker by SOURCE (`fn.toString()`),
 *    so a task may reference NOTHING from its outer scope — no module imports, no closure variables.
 *    Everything it needs must be inlined in the function body or passed via `deps:` (also serialized
 *    to source). NOTE: each `deps` entry is eval'd independently, so deps cannot reference each other;
 *    a cluster of mutually-recursive helpers must be nested inside one task function, not split across
 *    deps. See `map/utils/vmgReferenceDeltaTask.ts` for the canonical self-contained-task pattern.
 *  - **Dev-only stats.** `logPoolStats()` is a no-op unless `VITE_VERBOSE` is on; use it to confirm
 *    workers actually spawned (`mode:'workers'`) and the pool sized to `navigator.hardwareConcurrency`.
 */
import {
  pool as nodetsPool,
  parallel as nodetsParallel,
  transfer,
  isTransferred,
  transferablesOf,
  shared,
  type Pool,
  type PoolOptions,
  type CallOptions,
  type PoolStats,
  type StreamResult,
} from '@nodets/core';
import { createParallel, createParallelResource } from '@nodets/solid';

import { debug } from '../console';

/**
 * Product-wide pool defaults. `fallback:'inline'` is the non-negotiable safety net (also the lib default).
 * Typed as `Pick<..., 'fallback'>` rather than the full `PoolOptions`: the bare `PoolOptions` is
 * `PoolOptions<unknown>`, so spreading it into a `PoolOptions<S>` widens `setup` to `() => unknown`
 * and breaks assignability at the `createPool` call site.
 */
const DEFAULT_POOL_OPTIONS: Pick<PoolOptions, 'fallback'> = { fallback: 'inline' };

/**
 * Create a worker pool with RaceSight defaults applied. Prefer this over importing `pool` from
 * `@nodets/core` directly so the inline-fallback default and any future instrumentation stay uniform.
 */
export function createPool<A, R, S = unknown>(
  task: (arg: A, ctx: { state: S }) => R | Promise<R>,
  opts: PoolOptions<S> = {}
): Pool<A, R> {
  return nodetsPool<A, R, S>(task, { ...DEFAULT_POOL_OPTIONS, ...opts });
}

/** One-shot `parallel()` with RaceSight defaults (creates a pool, maps, disposes). */
export function runParallel<A, R>(
  task: (arg: A) => R | Promise<R>,
  items: readonly A[],
  opts: PoolOptions & CallOptions = {}
): Promise<R[]> {
  return nodetsParallel<A, R>(task, items, { ...DEFAULT_POOL_OPTIONS, ...opts });
}

/** Dev-only (`VITE_VERBOSE`) snapshot of a pool's state — confirms workers spawned vs inline fallback. */
export function logPoolStats(label: string, p: Pool<unknown, unknown>): void {
  const s: PoolStats = p.stats();
  debug(
    `[pool ${label}] mode=${s.mode} threads=${s.threads} busy=${s.busy} idle=${s.idle} ` +
      `queued=${s.queued} completed=${s.completed} failed=${s.failed}`
  );
}

// Re-export the SolidJS primitives (component-scoped pool, auto-disposed on cleanup) and the
// zero-copy / SharedArrayBuffer helpers so callers have a single import path.
export { createParallel, createParallelResource, transfer, isTransferred, transferablesOf, shared };
export type { Pool, PoolOptions, CallOptions, PoolStats, StreamResult };
